import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Briefcase,
  Building2,
  FileText,
  FolderKanban,
  Lightbulb,
  Loader2,
  Rocket,
  Target,
  Users,
  UserCircle,
  Clock,
  Search,
  AtSign,
  Hash,
} from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { useSearch } from "@/hooks/useSearch";
import { useCommandPaletteStore } from "@/stores/commandPaletteStore";
import { parseSearchInput } from "@/lib/searchParser";
import {
  EntityType,
  SearchContext,
  ENTITY_TYPE_LABELS,
  SearchResult,
} from "@/types/search";

const ENTITY_ICONS: Record<EntityType, React.ComponentType<{ className?: string }>> = {
  [EntityType.MARKET_INSIGHT]: Briefcase,
  [EntityType.OPPORTUNITY]: Target,
  [EntityType.SOLUTION]: FileText,
  [EntityType.STARTUP]: Rocket,
  [EntityType.PROJECT]: FolderKanban,
  [EntityType.COMMITTEE]: Users,
  [EntityType.ENTREPRENEUR]: UserCircle,
  [EntityType.APPLICATION]: FileText,
};

const TERMINUS_TYPE_SHORTCUTS = [
  { shortcut: "market", label: "Market Insights", labelKey: "commandPalette.types.marketInsights", type: EntityType.MARKET_INSIGHT, aliases: ["mi"] },
  { shortcut: "opp", label: "Opportunities", labelKey: "commandPalette.types.opportunities", type: EntityType.OPPORTUNITY, aliases: ["opportunity"] },
  { shortcut: "hyp", label: "Solutions", labelKey: "commandPalette.types.solutions", type: EntityType.SOLUTION, aliases: ["solution"] },
  { shortcut: "startup", label: "Startups", labelKey: "commandPalette.types.startups", type: EntityType.STARTUP, aliases: ["s"] },
  { shortcut: "project", label: "Projects", labelKey: "commandPalette.types.projects", type: EntityType.PROJECT, aliases: ["p"] },
  { shortcut: "committee", label: "Committees", labelKey: "commandPalette.types.committees", type: EntityType.COMMITTEE, aliases: ["c"] },
];

const FOUNDERSHUB_TYPE_SHORTCUTS = [
  { shortcut: "entrepreneur", label: "Entrepreneurs", labelKey: "commandPalette.types.entrepreneurs", type: EntityType.ENTREPRENEUR, aliases: ["ent", "founder"] },
  { shortcut: "application", label: "Applications", labelKey: "commandPalette.types.applications", type: EntityType.APPLICATION, aliases: ["app", "a"] },
  { shortcut: "project", label: "Projects", labelKey: "commandPalette.types.projects", type: EntityType.PROJECT, aliases: ["p", "proj"] },
];

function getEntityIcon(entityType: EntityType) {
  return ENTITY_ICONS[entityType] || Building2;
}

function groupResultsByType(results: SearchResult[]): Record<EntityType, SearchResult[]> {
  const groups: Record<EntityType, SearchResult[]> = {} as Record<EntityType, SearchResult[]>;

  for (const result of results) {
    if (!groups[result.entity_type]) {
      groups[result.entity_type] = [];
    }
    groups[result.entity_type].push(result);
  }

  return groups;
}

export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, close, addToHistory, getRecentSearches } = useCommandPaletteStore();
  const [inputValue, setInputValue] = useState("");
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState(0);

  const searchContext = useMemo(() => {
    return location.pathname.startsWith("/foundershub")
      ? SearchContext.FOUNDERSHUB
      : SearchContext.TERMINUS;
  }, [location.pathname]);

  const recentSearches = getRecentSearches(searchContext);

  const typeShortcuts = useMemo(() => {
    return searchContext === SearchContext.FOUNDERSHUB
      ? FOUNDERSHUB_TYPE_SHORTCUTS
      : TERMINUS_TYPE_SHORTCUTS;
  }, [searchContext]);

  const parsed = parseSearchInput(inputValue);
  const shouldSearch = isOpen && parsed.parsedQuery.length >= 2;

  const { data, isLoading, isFetching } = useSearch({
    query: parsed.parsedQuery,
    entityTypes: parsed.entityFilter,
    byId: parsed.isIdSearch,
    enabled: shouldSearch,
    context: searchContext,
  });

  // Detect if user is typing a @ command
  const isTypingAtCommand = inputValue.startsWith("@") && !inputValue.includes(" ");
  const atCommandFilter = isTypingAtCommand ? inputValue.slice(1).toLowerCase() : "";

  // Filter type shortcuts based on what user has typed and current context
  const filteredShortcuts = useMemo(() => {
    if (!isTypingAtCommand) return [];
    if (atCommandFilter === "") return typeShortcuts;
    return typeShortcuts.filter(s =>
      s.shortcut.startsWith(atCommandFilter) ||
      s.aliases.some(a => a.startsWith(atCommandFilter)) ||
      s.label.toLowerCase().includes(atCommandFilter)
    );
  }, [isTypingAtCommand, atCommandFilter, typeShortcuts]);

  // Detect if user is typing a # command
  const isTypingHashCommand = inputValue.startsWith("#") && inputValue.length === 1;

  // Reset selected index when dialog opens or context changes
  useEffect(() => {
    if (isOpen) {
      setSelectedShortcutIndex(0);
    }
  }, [isOpen, searchContext]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        useCommandPaletteStore.getState().toggle();
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Handle Tab key to cycle through @ suggestions (cycles through ALL type shortcuts)
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Tab" && isTypingAtCommand && typeShortcuts.length > 0) {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift+Tab goes backward
        const prevIndex = selectedShortcutIndex <= 0
          ? typeShortcuts.length - 1
          : selectedShortcutIndex - 1;
        const shortcut = typeShortcuts[prevIndex];
        setSelectedShortcutIndex(prevIndex);
        // No space - allows continued cycling
        setInputValue(`@${shortcut.shortcut}`);
      } else {
        // Tab goes forward - select current item and cycle through all types
        const shortcut = typeShortcuts[selectedShortcutIndex];
        // No space - allows continued cycling, user can press space or Enter to confirm
        setInputValue(`@${shortcut.shortcut}`);
        setSelectedShortcutIndex((prev) => (prev + 1) % typeShortcuts.length);
      }
    }
  }, [isTypingAtCommand, typeShortcuts, selectedShortcutIndex]);

  // Clear input when dialog opens or closes
  useEffect(() => {
    setInputValue("");
  }, [isOpen]);

  const handleSelect = useCallback((result: SearchResult) => {
    addToHistory(result, searchContext);
    navigate(result.route);
    close();
  }, [addToHistory, navigate, close, searchContext]);

  const handleSelectShortcut = useCallback((shortcut: string) => {
    setInputValue(`@${shortcut} `);
  }, []);

  const getPlaceholder = useCallback(() => {
    if (parsed.entityFilter && parsed.entityFilter.length > 0) {
      const types = parsed.entityFilter.map(et => ENTITY_TYPE_LABELS[et]).join(", ");
      return t('commandPalette.placeholderFiltered', { types });
    }
    if (parsed.isIdSearch) {
      return t('commandPalette.placeholderIdOrSlug');
    }
    return searchContext === SearchContext.FOUNDERSHUB
      ? t('commandPalette.placeholderEntrepreneurs')
      : t('commandPalette.placeholderEverything');
  }, [parsed.entityFilter, parsed.isIdSearch, searchContext, t]);

  const results = data?.results || [];
  const groupedResults = groupResultsByType(results);
  const hasResults = results.length > 0;
  const showRecent = !inputValue && recentSearches.length > 0;
  const isSearching = isLoading || isFetching;
  const showNoResults = !isSearching && !hasResults && shouldSearch;
  const showTypingHint = parsed.parsedQuery.length > 0 && parsed.parsedQuery.length < 2 && !isTypingAtCommand;
  const showEmptyState = !inputValue && !showRecent;
  const showAtSuggestions = isTypingAtCommand && filteredShortcuts.length > 0;
  const showHashHint = isTypingHashCommand;

  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => !open && close()} shouldFilter={false}>
      <CommandInput
        placeholder={getPlaceholder()}
        value={inputValue}
        onValueChange={setInputValue}
        onKeyDown={handleKeyDown}
      />
      <CommandList className="min-h-[300px] max-h-[400px]">
        {/* Empty state - no input */}
        {showEmptyState && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Search className="h-8 w-8 mb-4 opacity-40" />
            <p className="text-sm">
              {searchContext === SearchContext.FOUNDERSHUB
                ? t('commandPalette.startTypingEntrepreneurs')
                : t('commandPalette.startTypingAll')}
            </p>
            <div className="flex items-center gap-4 mt-3 text-xs">
              <span>
                <kbd className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">@</kbd>
                <span className="ml-1.5 opacity-70">{t('commandPalette.filterByTypeHint')}</span>
              </span>
              <span>
                <kbd className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">#</kbd>
                <span className="ml-1.5 opacity-70">{t('commandPalette.searchByIdHint')}</span>
              </span>
            </div>
          </div>
        )}

        {/* @ command autocomplete */}
        {showAtSuggestions && (
          <CommandGroup heading={t('commandPalette.filterByTypeHeading')}>
            {filteredShortcuts.map((shortcut) => {
              const Icon = ENTITY_ICONS[shortcut.type];
              return (
                <CommandItem
                  key={shortcut.shortcut}
                  value={`@${shortcut.shortcut}`}
                  onSelect={() => handleSelectShortcut(shortcut.shortcut)}
                  className="flex items-center gap-3 py-2"
                >
                  <AtSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium">@{shortcut.shortcut}</span>
                    <span className="text-xs text-muted-foreground">
                      {t('commandPalette.search')} {t(shortcut.labelKey, shortcut.label)}
                      {shortcut.aliases.length > 0 && (
                        <span className="ml-2 opacity-60">
                          ({t('commandPalette.also')}: {shortcut.aliases.map(a => `@${a}`).join(", ")})
                        </span>
                      )}
                    </span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* # command hint */}
        {showHashHint && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Hash className="h-8 w-8 mb-4 opacity-40" />
            <p className="text-sm">{t('commandPalette.searchByIdOrSlug')}</p>
            <p className="text-xs mt-2 opacity-70">
              {t('commandPalette.enterIdOrSlugDesc')}
            </p>
          </div>
        )}

        {/* Typing hint - need more characters */}
        {showTypingHint && (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            {t('commandPalette.typeAtLeast2')}
          </div>
        )}

        {/* Loading state */}
        {isSearching && shouldSearch && (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('commandPalette.searching')}
          </div>
        )}

        {/* No results */}
        {showNoResults && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Search className="h-8 w-8 mb-4 opacity-40" />
            <p className="text-sm">{t('commandPalette.noResultsFor', { query: parsed.parsedQuery })}</p>
          </div>
        )}

        {/* Recent searches */}
        {showRecent && (
          <CommandGroup heading={t('commandPalette.recent')}>
            {recentSearches.map((result) => {
              const Icon = getEntityIcon(result.entity_type);
              return (
                <CommandItem
                  key={`recent-${result.id}`}
                  value={`recent-${result.id}-${result.title}`}
                  onSelect={() => handleSelect(result)}
                  className="flex items-center gap-3 py-2"
                >
                  <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="truncate font-medium">{result.title}</span>
                    {result.subtitle && (
                      <span className="text-xs text-muted-foreground truncate">
                        {result.subtitle}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground flex-shrink-0">
                    {ENTITY_TYPE_LABELS[result.entity_type]}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Search results */}
        {hasResults && !isSearching && (
          <>
            {showRecent && <CommandSeparator />}
            {Object.entries(groupedResults).map(([entityType, typeResults]) => (
              <CommandGroup
                key={entityType}
                heading={ENTITY_TYPE_LABELS[entityType as EntityType]}
              >
                {typeResults.map((result) => {
                  const Icon = getEntityIcon(result.entity_type);
                  return (
                    <CommandItem
                      key={result.id}
                      value={`${result.id}-${result.title}`}
                      onSelect={() => handleSelect(result)}
                      className="flex items-center gap-3 py-2"
                    >
                      <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate font-medium">{result.title}</span>
                        {result.subtitle && (
                          <span className="text-xs text-muted-foreground truncate">
                            {result.subtitle}
                          </span>
                        )}
                      </div>
                      {result.status && (
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground px-1.5 py-0.5 rounded bg-muted flex-shrink-0">
                          {result.status}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
